import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { EmptyState, PrimaryButton, SecondaryButton, StatusPill, TextButton } from '@/lib';
import { usePatchTenant, useTenant } from './queries';
import { ImpersonateModal } from './ImpersonateModal';

export function TenantDetail() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id ?? '';
  const navigate = useNavigate();
  const tenant = useTenant(id);
  const patch = usePatchTenant(id);
  const [impersonating, setImpersonating] = useState(false);

  if (tenant.isError || !tenant.data) {
    if (tenant.isLoading) {
      return <p className="text-fs-body text-gray-700">Loading…</p>;
    }
    return (
      <EmptyState
        variant="error"
        title="Tenant not found"
        body={String(tenant.error ?? 'No record for this id.')}
        cta={{
          label: 'Back to tenants',
          onClick: () => navigate({ to: '/radiant/tenants' }),
        }}
      />
    );
  }

  const t = tenant.data;
  const isActive = t.status === 'active';
  const isSuspended = t.status === 'suspended';

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-baseline justify-between gap-sp-3">
        <div>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Radiant tenant
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">{t.display_name}</h1>
          <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">{t.id}</p>
        </div>
        <StatusPill
          level={isActive ? 'healthy' : isSuspended ? 'warn' : 'critical'}
          label={t.status}
        />
      </header>

      <section
        aria-label="Tenant summary"
        className="grid grid-cols-1 gap-sp-3 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6 sm:grid-cols-2"
      >
        <Row label="Contact" value={t.contact_email} />
        <Row label="Plan" value={t.plan} />
        <Row label="Region" value={t.region || '—'} />
        <Row label="Created" value={new Date(t.created_at).toLocaleString()} />
        {t.suspended_at ? (
          <Row
            label="Suspended"
            value={new Date(t.suspended_at).toLocaleString()}
          />
        ) : null}
      </section>

      <section
        aria-label="Lifecycle actions"
        className="flex items-center gap-sp-3"
      >
        {isActive ? (
          <SecondaryButton
            label={patch.isPending ? 'Suspending…' : 'Suspend tenant'}
            onClick={() => patch.mutate({ status: 'suspended' })}
            disabled={patch.isPending}
          />
        ) : null}
        {isSuspended ? (
          <PrimaryButton
            label={patch.isPending ? 'Resuming…' : 'Resume tenant'}
            onClick={() => patch.mutate({ status: 'active' })}
            disabled={patch.isPending}
          />
        ) : null}
        <TextButton
          label="Mark churned"
          onClick={() => {
            if (window.confirm(`Mark ${t.display_name} as churned? This is reversible.`)) {
              patch.mutate({ status: 'churned' });
            }
          }}
          disabled={patch.isPending}
        />
        {patch.isError ? (
          <span role="alert" className="text-fs-meta text-critical">
            {String(patch.error)}
          </span>
        ) : null}
      </section>

      <section
        aria-label="Impersonation"
        className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      >
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Impersonate
        </p>
        <p className="mt-sp-2 text-fs-body text-gray-700">
          Generate a scoped session inside this tenant. Audit log
          records both your Radiant identity and the on-behalf-of
          tenant admin for every mutation.
        </p>
        <div className="mt-sp-3">
          <PrimaryButton
            label="Start impersonation…"
            onClick={() => setImpersonating(true)}
            disabled={t.status === 'churned'}
          />
        </div>
      </section>

      {impersonating ? (
        <ImpersonateModal
          tenantId={t.id}
          tenantName={t.display_name}
          onClose={() => setImpersonating(false)}
        />
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-sp-1">
      <span className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
        {label}
      </span>
      <span className="text-fs-body text-gray-900">{value}</span>
    </div>
  );
}
